import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Progress from "../../../../components/wizard/Progress";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import useMutation from "../../../../hooks/useMutation";
import useNotification from "../../../../hooks/notification";
import LocationTypeSelection from "./LocationTypeSelection";
import MobileLocationCreation from "./MobileLocationCreation";
import BaseLocationCreation from "./BaseLocationCreation";
import {
  WAREHOUSE_LOCATION_CREATE,
  MOBILE_WAREHOUSE_LOCATION_CREATE,
  WAREHOUSE_LOCATIONS_BULK_CREATE,
  MOBILE_WAREHOUSE_LOCATIONS_BULK_CREATE,
  WAREHOUSE_LOCATION_ADD_MISSING_CENTERS,
} from "../../../../constants/graphql/mutations";
import {
  expandLocationNameRange,
  EXISTING_CENTER_SLOT,
  MAX_LOCATION_NAMES,
} from "../../../../utils/locationNameRange";
import { GUTTER, text } from "./modalStyles";

const rangeError = (from, to) => expandLocationNameRange(from, to).error;

const isExistingCenterSlot = slot => slot === EXISTING_CENTER_SLOT;

const namingRules = {
  creatingMode: Yup.string().oneOf(["single", "multiple"]).required("Required"),
  locationName: Yup.string().when(["creatingMode", "slot"], {
    is: (creatingMode, slot) => creatingMode === "single" && !isExistingCenterSlot(slot),
    then: schema => schema.required("Required"),
    otherwise: schema => schema.notRequired(),
  }),
  locationFrom: Yup.string().when(["creatingMode", "slot"], {
    is: (creatingMode, slot) => creatingMode === "multiple" && !isExistingCenterSlot(slot),
    then: schema => schema.required("Required"),
    otherwise: schema => schema.notRequired(),
  }),
  locationTo: Yup.string().when(["creatingMode", "slot"], {
    is: (creatingMode, slot) => creatingMode === "multiple" && !isExistingCenterSlot(slot),
    then: schema =>
      schema.required("Required").test("valid-range", function (value) {
        const error = rangeError(this.parent.locationFrom, value);
        return error ? this.createError({ message: error }) : true;
      }),
    otherwise: schema => schema.notRequired(),
  }),
};

const baseLocationSchema = Yup.object().shape({
  type: Yup.string().required("Required"),
  ...namingRules,
  locationType: Yup.string().required("Required"),
  sqFtOfSlot: Yup.number().when("slot", {
    is: isExistingCenterSlot,
    then: schema =>
      schema
        .transform((value, original) => (original === "" || original == null ? undefined : value))
        .notRequired(),
    otherwise: schema => schema.positive("Square feet must be greater than 0").required("Required"),
  }),
  slot: Yup.string().when("locationType", {
    is: "rack",
    then: schema => schema.required("Required"),
    otherwise: schema => schema.notRequired(),
  }),
  customSlot: Yup.string().when("slot", {
    is: "Custom",
    then: schema => schema.required("Required"),
    otherwise: schema => schema.notRequired(),
  }),
  leftLocationIds: Yup.array().when("slot", {
    is: isExistingCenterSlot,
    then: schema => schema.min(1, "Select at least one location"),
    otherwise: schema => schema.notRequired(),
  }),
});

const mobileLocationSchema = Yup.object().shape({
  type: Yup.string().required("Required"),
  ...namingRules,
  locationType: Yup.string().required("Required"),
  sqFtOfSlot: Yup.number()
    .positive("Square feet must be greater than 0")
    .required("Required"),
});

const styles = {
  dialogTitle: {
    m: 0,
    px: GUTTER,
    pt: GUTTER,
    pb: 0,
  },
  closeButton: {
    position: "absolute",
    right: 12,
    top: 12,
    color: "text.secondary",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: 0,
  },
  dialogContent: {
    px: GUTTER,
    pt: 2.5,
    pb: 0,
    // min-height 0 is what allows a flex child to shrink below its content and
    // scroll, rather than forcing the parent to grow.
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
  },
  // Type step has no progress bar, so keep the cards off the dialog edge.
  dialogContentMain: {
    pb: GUTTER,
  },
  // Radios, type, slot, name, sq ft and a short preview. Holding this height
  // from the first pick means those fields fill the gap instead of shoving
  // the footer down each time.
  dialogContentCreate: {
    minHeight: 400,
  },
  dialogActions: {
    px: 2,
    py: 1.5,
    gap: 1,
    justifyContent: "flex-end",
  },
  actionButton: {
    px: 3,
    minWidth: 104,
  },
};

const LOCATION_WIZARD_STEPS = 2;

const MAIN_PROMPT = "What type of location would you like to create?";

const getMutationErrorMessage = errors => {
  if (!errors?.length) return null;

  return errors.map(error => error.message).filter(Boolean).join(", ") || "Unable to create location";
};

const slotValueFor = values => {
  if (values.locationType !== "rack") return "n/a";
  if (values.slot === "Custom") return values.customSlot || "n/a";
  if (isExistingCenterSlot(values.slot)) return "n/a";
  return values.slot || "n/a";
};

const sharedLocationVariables = values => ({
  locationType: values.locationType,
  sqFtOfSlot: values.sqFtOfSlot,
  slot: slotValueFor(values),
  zone: values.zone || "n/a",
  aisle: values.aisle || "n/a",
  shelf: values.shelf || "n/a",
  rack: values.rack || "n/a",
  dimensions: values.dimensions,
});

export default function LocationSelectionModal({
  warehouseId,
  step,
  onStepChange,
  onClose,
  onCreated,
}) {
  const [createWarehouseLocation] = useMutation(WAREHOUSE_LOCATION_CREATE);
  const [createMobileWarehouseLocation] = useMutation(MOBILE_WAREHOUSE_LOCATION_CREATE);
  const [bulkCreateLocations] = useMutation(WAREHOUSE_LOCATIONS_BULK_CREATE);
  const [bulkCreateMobileLocations] = useMutation(MOBILE_WAREHOUSE_LOCATIONS_BULK_CREATE);
  const [addMissingCenters] = useMutation(WAREHOUSE_LOCATION_ADD_MISSING_CENTERS);
  const { notifyError, notifySuccess } = useNotification();

  const initialValues = {
    type: step === "main" ? "" : step,
    creatingMode: "",
    locationType: "",
    locationName: "",
    locationFrom: "",
    locationTo: "",
    slot: "",
    customSlot: "",
    sqFtOfSlot: "",
    zone: "",
    dimensions: [],
    leftLocationIds: [],
    missingCenterNames: [],
  };

  const getValidationSchema = currentStep => {
    if (currentStep === "mobile") return mobileLocationSchema;
    if (currentStep === "base") return baseLocationSchema;
  };

  // onCreated owns the close so success replaces the history entry instead of
  // pushing another one.
  const finishCreate = () => {
    if (onCreated) onCreated();
    else onClose();
  };

  // Both branches accept a From/To range, so resolve it once up front.
  const resolveBulkNames = values => {
    const { names, error } = expandLocationNameRange(values.locationFrom, values.locationTo);

    if (error || names.length === 0) {
      notifyError(error || "Enter a valid From and To");
      return null;
    }
    if (names.length > MAX_LOCATION_NAMES) {
      notifyError(`Cannot create more than ${MAX_LOCATION_NAMES} locations at once`);
      return null;
    }

    return names;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const isMultiple = values.creatingMode === "multiple";

    try {
      if (step === "mobile") {
        if (isMultiple) {
          const names = resolveBulkNames(values);
          if (!names) return;

          const response = await bulkCreateMobileLocations({
            variables: {
              warehouseId,
              locationNames: names,
              locationType: values.locationType,
              sqFtOfSlot: values.sqFtOfSlot,
              zone: values.zone || "n/a",
              dimensions: values.dimensions,
            },
          });

          const errorMessage = getMutationErrorMessage(
            response?.data?.mobileWarehouseLocationBulkCreate?.errors
          );
          if (errorMessage) {
            notifyError(errorMessage);
            return;
          }

          const created = response?.data?.mobileWarehouseLocationBulkCreate?.mobileWarehouseLocations || [];
          notifySuccess(`${created.length} mobile locations successfully created`);
          finishCreate();
          return;
        }

        const response = await createMobileWarehouseLocation({
          variables: {
            warehouseId,
            locationName: values.locationName,
            locationType: values.locationType,
            sqFtOfSlot: values.sqFtOfSlot,
            dimensions: values.dimensions,
          },
        });

        const errorMessage = getMutationErrorMessage(response?.data?.mobileWarehouseLocationCreate?.errors);
        if (errorMessage) {
          notifyError(errorMessage);
          return;
        }

        notifySuccess(`Mobile location "${values.locationName}" successfully created`);
        finishCreate();
        return;
      }

      if (isExistingCenterSlot(values.slot)) {
        const response = await addMissingCenters({
          variables: {
            warehouseId,
            leftLocationIds: values.leftLocationIds,
          },
        });

        const errorMessage = getMutationErrorMessage(
          response?.data?.warehouseLocationAddMissingCenters?.errors
        );
        if (errorMessage) {
          notifyError(errorMessage);
          return;
        }

        const createdLocations =
          response?.data?.warehouseLocationAddMissingCenters?.warehouseLocations || [];
        notifySuccess(`${createdLocations.length} locations successfully created`);
        finishCreate();
        return;
      }

      if (isMultiple) {
        const names = resolveBulkNames(values);
        if (!names) return;

        const response = await bulkCreateLocations({
          variables: {
            warehouseId,
            locationNames: names,
            ...sharedLocationVariables(values),
          },
        });

        const errorMessage = getMutationErrorMessage(response?.data?.warehouseLocationsBulkCreate?.errors);
        if (errorMessage) {
          notifyError(errorMessage);
          return;
        }

        const createdLocations = response?.data?.warehouseLocationsBulkCreate?.warehouseLocations || [];
        notifySuccess(`${createdLocations.length} locations successfully created`);
        finishCreate();
        return;
      }

      const response = await createWarehouseLocation({
        variables: {
          warehouseId,
          locationName: values.locationName,
          ...sharedLocationVariables(values),
        },
      });

      const errorMessage = getMutationErrorMessage(response?.data?.warehouseLocationCreate?.errors);
      if (errorMessage) {
        notifyError(errorMessage);
        return;
      }

      const createdLocations = response?.data?.warehouseLocationCreate?.warehouseLocations || [];

      if (createdLocations.length > 1) {
        const locationNames = createdLocations.map(loc => `"${loc.locationName}"`).join(", ");
        notifySuccess(`${createdLocations.length} locations successfully created: ${locationNames}`);
      } else {
        notifySuccess(`Base location "${values.locationName}" successfully created`);
      }
      finishCreate();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="warehouse-location-assistant"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema(step)}
        onSubmit={handleSubmit}
      >
        {({ values, isSubmitting, setFieldValue }) => (
          <Form style={styles.form}>
            <DialogTitle component="div" sx={styles.dialogTitle}>
              <Typography component="h2" sx={text.title}>
                Create New Location
              </Typography>
              <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={[
                styles.dialogContent,
                step === "main" ? styles.dialogContentMain : styles.dialogContentCreate,
              ]}
            >
              {step === "main" && (
                <>
                  <Typography sx={{ ...text.help, mb: 2 }}>{MAIN_PROMPT}</Typography>
                  <LocationTypeSelection
                    locationType={values.type}
                    onTypeSelection={type => {
                      setFieldValue("type", type);
                      // The two branches share no location types, so a value
                      // picked in one is invalid in the other.
                      if (type !== values.type) {
                        setFieldValue("locationType", "");
                        setFieldValue("slot", "");
                        setFieldValue("customSlot", "");
                      }
                      onStepChange(type);
                    }}
                  />
                </>
              )}
              {step === "mobile" && <MobileLocationCreation />}
              {step === "base" && <BaseLocationCreation warehouseId={warehouseId} />}
            </DialogContent>
            {step !== "main" && (
              <>
                <Progress step={1} steps={LOCATION_WIZARD_STEPS} />
                <DialogActions sx={styles.dialogActions}>
                  <Button
                    onClick={() => onStepChange("main")}
                    variant="outlined"
                    color="primary"
                    sx={styles.actionButton}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                    sx={styles.actionButton}
                  >
                    Submit
                  </Button>
                </DialogActions>
              </>
            )}
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
